import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components';
import api from '@/utils/api';
import './SpotlightAnalytics.scss';

interface AnalyticsSummary {
  byType: Record<string, {
    totalCount: number;
    totalRevenue: number;
    events: {
      view: number;
      click: number;
      like: number;
      comment: number;
      share: number;
    };
  }>;
  overall: {
    totalSpotlights: number;
    totalRevenue: number;
    totalViews: number;
    totalClicks: number;
    totalEngagements: number;
  };
}

const SpotlightAnalyticsAdmin: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, selectedType]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);
      if (selectedType !== 'all') params.append('spotlightType', selectedType);

      const response = await api.get(`/admin/spotlight/analytics?${params.toString()}`);
      setAnalytics(response.data.summary);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics');
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const typeLabels: Record<string, string> = {
    all: 'All Types',
    post: 'Posts',
    profile: 'Profiles',
    dm_access: 'DM Access',
  };

  const calculateEngagementRate = (clicks: number, views: number) => {
    return views > 0 ? ((clicks / views) * 100).toFixed(2) : '0.00';
  };

  if (isLoading) {
    return <LoadingSpinner size="large" message="Loading analytics..." fullHeight />;
  }

  return (
    <div className="spotlight-analytics-admin">
      <div className="admin-header">
        <div>
          <h1>Spotlight Analytics Dashboard</h1>
          <p className="subtitle">Monitor spotlight performance and revenue</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="filters-card">
        <div className="filter-group">
          <label>Spotlight Type</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="post">Post Spotlights</option>
            <option value="profile">Profile Spotlights</option>
            <option value="dm_access">DM Access Spotlights</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
          />
        </div>

        <button
          className="btn-clear"
          onClick={() => {
            setDateRange({ startDate: '', endDate: '' });
            setSelectedType('all');
          }}
        >
          Clear Filters
        </button>
      </div>

      {analytics && (
        <>
          {/* Overall Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-content">
                <div className="metric-label">Total Spotlights</div>
                <div className="metric-value">{analytics.overall.totalSpotlights.toLocaleString()}</div>
              </div>
            </div>

            <div className="metric-card revenue">
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <div className="metric-label">Total Revenue</div>
                <div className="metric-value">{analytics.overall.totalRevenue.toFixed(2)} KLV</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">👁️</div>
              <div className="metric-content">
                <div className="metric-label">Total Views</div>
                <div className="metric-value">{analytics.overall.totalViews.toLocaleString()}</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">🖱️</div>
              <div className="metric-content">
                <div className="metric-label">Total Clicks</div>
                <div className="metric-value">{analytics.overall.totalClicks.toLocaleString()}</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">❤️</div>
              <div className="metric-content">
                <div className="metric-label">Total Engagements</div>
                <div className="metric-value">{analytics.overall.totalEngagements.toLocaleString()}</div>
                <div className="metric-subtext">likes + comments + shares</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">📈</div>
              <div className="metric-content">
                <div className="metric-label">Avg Engagement Rate</div>
                <div className="metric-value">
                  {calculateEngagementRate(analytics.overall.totalClicks, analytics.overall.totalViews)}%
                </div>
              </div>
            </div>
          </div>

          {/* By Type Breakdown */}
          <div className="type-breakdown">
            <h2>Performance by Type</h2>
            <div className="breakdown-grid">
              {Object.entries(analytics.byType).map(([type, data]) => (
                <div key={type} className="type-card">
                  <h3>{typeLabels[type] || type}</h3>

                  <div className="type-metrics">
                    <div className="type-metric">
                      <span className="label">Count:</span>
                      <span className="value">{data.totalCount}</span>
                    </div>
                    <div className="type-metric">
                      <span className="label">Revenue:</span>
                      <span className="value">{data.totalRevenue.toFixed(2)} KLV</span>
                    </div>
                    <div className="type-metric">
                      <span className="label">Views:</span>
                      <span className="value">{data.events.view.toLocaleString()}</span>
                    </div>
                    <div className="type-metric">
                      <span className="label">Clicks:</span>
                      <span className="value">{data.events.click.toLocaleString()}</span>
                    </div>
                    <div className="type-metric">
                      <span className="label">Engagement Rate:</span>
                      <span className="value">
                        {calculateEngagementRate(data.events.click, data.events.view)}%
                      </span>
                    </div>
                  </div>

                  <div className="engagement-breakdown">
                    <h4>Engagement Details</h4>
                    <div className="engagement-stats">
                      <div className="stat">
                        <span className="icon">❤️</span>
                        <span>{data.events.like}</span>
                      </div>
                      <div className="stat">
                        <span className="icon">💬</span>
                        <span>{data.events.comment}</span>
                      </div>
                      <div className="stat">
                        <span className="icon">🔄</span>
                        <span>{data.events.share}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpotlightAnalyticsAdmin;
